import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plane, Loader2, Users, ArrowRight, Radio, Gavel, Sparkles,
  MapPin, Clock, TrendingDown, TrendingUp, ShieldCheck, ChevronRight,
} from "lucide-react";
import BackToCockpit from "@/components/BackToCockpit";

/**
 * /charter-dashboard
 *
 * The Charter Client's home base. Shows every trip request they've filed
 * with PJP and the live bids each has attracted. Actions:
 *   • Kick off a new quote → /quote
 *   • Award a winning bid    → POST /api/charter-requests/{id}/award/{bid}
 *
 * Server endpoint: GET /api/charter-requests/mine
 *   Returns [{...request, bids: [...], summary: {bid_count, lowest, median, highest}}]
 */
export default function CharterClientDashboard() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dashboard is visible to anyone with the charter_client hat (so they
  // can see the upsell banner and their own past RFQs), but member-only
  // features require the paid subscription flag.
  const isCharterMember = user?.membership_tier_slug === "charter_client";

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      nav("/login?next=/charter-dashboard");
      return;
    }
    (async () => {
      try {
        const r = await api.get("/charter-requests/mine");
        setRows(r.data.requests || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load your trips");
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, user, nav]);

  const award = async (rid, bid_id) => {
    try {
      await api.post(`/charter-requests/${rid}/award/${bid_id}`);
      toast.success("Bid awarded — operator notified.");
      const r = await api.get("/charter-requests/mine");
      setRows(r.data.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not award bid");
    }
  };

  const stats = useMemo(() => {
    if (!rows) return null;
    const open = rows.filter((r) => r.status === "open");
    const awarded = rows.filter((r) => r.status === "awarded");
    const totalBids = rows.reduce((s, r) => s + (r.summary?.bid_count || 0), 0);
    // Estimated savings vs. worst-of-band on awarded trips.
    let savingsSum = 0;
    for (const r of awarded) {
      if (r.summary?.highest_bid_usd && r.summary?.lowest_bid_usd) {
        savingsSum += (r.summary.highest_bid_usd - r.summary.lowest_bid_usd);
      }
    }
    return { open: open.length, awarded: awarded.length, totalBids, savingsSum };
  }, [rows]);

  if (loading || authLoading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-14 text-center text-white/60">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="charter-client-dashboard">
      <BackToCockpit />

      {/* Hero */}
      <div className="mt-2 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// Charter Client · Command deck</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter mt-1 leading-none">
            Your trips, <span className="text-cyan">live</span>.
          </h1>
          <div className="text-white/60 mt-2 text-sm">
            Every RFQ you file, every operator bid you attract — all in one place.
          </div>
        </div>
        <Link
          to="/quote"
          data-testid="charter-dash-new-quote"
          className="btn-hud btn-hud-solid"
        >
          <Plane className="h-3.5 w-3.5" /> START A NEW QUOTE <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Not-a-member gate */}
      {!isCharterMember && (
        <div className="mt-6 rounded-lg border border-cyan/40 bg-cyan/[0.06] p-4 flex items-center gap-3 flex-wrap" data-testid="charter-dash-upsell">
          <ShieldCheck className="h-5 w-5 text-cyan" />
          <div className="flex-1 min-w-[240px]">
            <div className="text-sm text-white/90">You're viewing your RFQs, but Charter Client members get PJP volume pricing on top of every bid.</div>
            <div className="mono text-[10px] tracking-widest uppercase text-white/50 mt-1">$5,000 / year · cancel anytime</div>
          </div>
          <Link to="/charter-membership" data-testid="charter-dash-upsell-cta" className="btn-hud btn-hud-solid">
            <Sparkles className="h-3.5 w-3.5" /> JOIN CHARTER CLIENT
          </Link>
        </div>
      )}

      {/* Stats strip */}
      {stats && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="charter-dash-stats">
          <StatPill icon={Radio}  label="Open RFQs"   value={stats.open}    tone="cyan" testId="stat-open" />
          <StatPill icon={Gavel}  label="Live bids"   value={stats.totalBids} tone="white" testId="stat-bids" />
          <StatPill icon={ShieldCheck} label="Awarded" value={stats.awarded} tone="avail" testId="stat-awarded" />
          <StatPill icon={TrendingDown} label="Est. saved" value={stats.savingsSum > 0 ? `$${Math.round(stats.savingsSum / 1000)}k` : "—"} sub="vs. worst-of-band" tone="avail" testId="stat-saved" />
        </div>
      )}

      {/* Trips list */}
      <div className="mt-8 space-y-4" data-testid="charter-dash-trips">
        {rows && rows.length === 0 ? (
          <EmptyState />
        ) : (
          rows.map((r) => (
            <TripCard key={r.id} req={r} onAward={(bidId) => award(r.id, bidId)} isMember={isCharterMember} />
          ))
        )}
      </div>
    </div>
  );
}

function StatPill({ icon: Icon, label, value, sub, tone = "white", testId }) {
  const toneCls = tone === "cyan" ? "text-cyan" : tone === "avail" ? "text-avail" : "text-white";
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="mono text-[9px] tracking-[0.24em] uppercase text-white/50">{label}</span>
        <Icon className={`h-3.5 w-3.5 ${toneCls}`} />
      </div>
      <div className={`font-display text-2xl font-bold mt-1 ${toneCls}`}>{value}</div>
      {sub && <div className="mono text-[9px] tracking-widest uppercase text-white/40 mt-0.5">{sub}</div>}
    </div>
  );
}

function TripCard({ req, onAward, isMember }) {
  const [open, setOpen] = useState(false);
  const routing = (req.routing || [req.departure_icao, req.destination_icao]).join(" → ");
  const bids = req.bids || [];
  const s = req.summary || {};
  const fmt = (n) => n != null ? n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—";

  const statusColor = req.status === "awarded" ? "text-avail" : req.status === "open" ? "text-cyan" : "text-white/40";
  const isAwarded = req.status === "awarded";

  return (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
      data-testid={`charter-trip-${req.id}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <MapPin className="h-3.5 w-3.5 text-cyan" />
            <span className="font-display text-lg font-bold text-white">{routing}</span>
            <span className={`mono text-[10px] tracking-widest uppercase ${statusColor}`}>
              · {req.status}
            </span>
          </div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mt-1 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {req.trip_start}</span>
            <span>{req.passengers} pax</span>
            {req.aircraft_class_pref && <span className="text-cyan">{req.aircraft_class_pref}</span>}
            {req.budget_usd && <span>· median target {fmt(req.budget_usd)}</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="mono text-[9px] tracking-widest uppercase text-white/50">Bids received</div>
          <div className="font-display text-3xl font-bold text-cyan leading-none">{s.bid_count || 0}</div>
          {s.lowest_bid_usd != null && (
            <div className="mono text-[10px] tracking-widest uppercase text-avail mt-1">
              low {fmt(s.lowest_bid_usd)} · high {fmt(s.highest_bid_usd)}
            </div>
          )}
        </div>
      </div>

      {/* Toggle bids */}
      {bids.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`charter-trip-${req.id}-toggle`}
            className="mt-3 mono text-[10px] tracking-widest uppercase text-cyan hover:text-white inline-flex items-center gap-1"
          >
            {open ? "HIDE" : "VIEW"} OPERATOR BIDS <ChevronRight className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
          {open && (
            <div className="mt-3 space-y-2" data-testid={`charter-trip-${req.id}-bids`}>
              {bids
                .slice()
                .sort((a, b) => (a.price_usd || 0) - (b.price_usd || 0))
                .map((b, i) => {
                  const isWinner = req.awarded_bid_id === b.id;
                  const isBestPrice = s.lowest_bid_usd != null && b.price_usd === s.lowest_bid_usd;
                  return (
                    <div
                      key={b.id}
                      className={`rounded-lg border p-3 flex items-center justify-between flex-wrap gap-3 ${isWinner ? "border-avail/60 bg-avail/[0.08]" : "border-white/10 bg-obsidian/50"}`}
                      data-testid={`charter-bid-${b.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {isBestPrice && !isAwarded && (
                            <span className="mono text-[9px] tracking-widest uppercase text-avail bg-avail/10 border border-avail/30 rounded px-1.5 py-0.5">Best price</span>
                          )}
                          {isWinner && (
                            <span className="mono text-[9px] tracking-widest uppercase text-avail bg-avail/15 border border-avail/40 rounded px-1.5 py-0.5">Awarded</span>
                          )}
                          <span className="font-display text-lg font-bold text-white">{fmt(b.price_usd)}</span>
                          <span className="text-white/60 text-sm">{b.operator_company || "Operator"}</span>
                        </div>
                        <div className="mono text-[10px] tracking-widest uppercase text-white/50 mt-1">
                          {b.aircraft_snapshot?.tail_number} · {b.aircraft_snapshot?.type_code} · {b.aircraft_snapshot?.seats || "—"} pax
                        </div>
                        {b.notes && <div className="text-[12px] text-white/65 mt-1">{b.notes}</div>}
                      </div>
                      {!isAwarded && (
                        <button
                          onClick={() => onAward(b.id)}
                          data-testid={`charter-bid-${b.id}-award`}
                          className="btn-hud btn-hud-solid"
                        >
                          AWARD · BOOK <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </>
      )}

      {bids.length === 0 && (
        <div className="mt-3 mono text-[10px] tracking-widest uppercase text-white/40 flex items-center gap-1">
          <Radio className="h-3 w-3 text-cyan animate-pulse" /> Waiting for operators to bid — usually 20–90 min in business hours
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-cyan/30 bg-cyan/[0.03] p-8 text-center" data-testid="charter-dash-empty">
      <Plane className="h-8 w-8 text-cyan mx-auto mb-3" />
      <div className="font-display text-2xl font-bold text-white">No trips filed yet.</div>
      <div className="text-white/60 text-sm mt-1 max-w-md mx-auto">
        Price a route on the Instant PJ Quote page and click <b className="text-cyan">GET OPERATOR DIRECT BIDS</b> to fire your first RFQ. Bids from PJP operators land right here.
      </div>
      <Link
        to="/quote"
        data-testid="charter-dash-empty-cta"
        className="btn-hud btn-hud-solid inline-flex mt-4"
      >
        <Plane className="h-3.5 w-3.5" /> PRICE A TRIP <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
